import { TFunction } from 'i18next';
import { transliterateCyrillic } from '../transliterate-cyrillic';
import { isNotEmpty, UseFormInput } from '@mantine/form';
import { ArgumentTypes } from '@/shared/lib/utils/type-helpers/argument-types';
type InputValues = Partial<{
  [key: string]: any;
}>;
export const validateInputs = (
  t: TFunction,
  labelField = 'label',
  nameField = 'name',
) => ({
  [labelField]: isNotEmpty(t('form.required', { ns: 'validation' })),
  [nameField]: (v, values) => {
    const auto = transliterateCyrillic(values[labelField]);

    if (!v) return auto ? undefined : t('form.required', { ns: 'validation' });

    if (v.match(/[^a-zA-Z0-9\s-_]/)) {
      return `${t('form.latinWithScores', { ns: 'validation' })} ${t(
        'form.required',
        {
          ns: 'validation',
        },
      )}`;
    }

    return undefined;
  },
});

export const enhanceInputProps =
  <T extends InputValues>(labelField = 'label', nameField = 'name') =>
  (payload: ArgumentTypes<UseFormInput<T>['enhanceGetInputProps']>[0]) => {
    const onChange = payload.inputProps.onChange;
    const onFocus = payload.inputProps.onFocus;
    const onBlur = payload.inputProps.onBlur;
    if (payload.field === labelField)
      return {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange(e);
          const next = e.target.value;
          const nameNode = payload.form.getInputNode(
            nameField,
          ) as HTMLInputElement | null;
          nameNode?.setAttribute('placeholder', transliterateCyrillic(next));
        },
      };
    if (payload.field === nameField) {
      return {
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          onFocus?.(e);
          const label = payload.form.getValues()[labelField];
          const auto = transliterateCyrillic(label);
          const node = e.currentTarget;
          if (!node.value) {
            node.value = auto;
            onChange?.(auto);
          }
        },

        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          onBlur?.(e);

          const label = payload.form.getValues()[labelField];
          const auto = transliterateCyrillic(label);

          if (e.currentTarget.value === auto) {
            e.currentTarget.value = '';
            onChange?.('');
          }
        },
        placeholder: transliterateCyrillic(
          payload.form.getValues()[labelField],
        ),
      };
    }

    return {};
  };

export const transformInputValues =
  <T extends InputValues>(labelField = 'label', nameField = 'name') =>
  (values: T) => ({
    ...values,
    [nameField]: values[nameField] || transliterateCyrillic(values[labelField]),
  });